import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { ExternalBankAccount } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from "react-plaid-link";
import { externalBankAccountsApi } from "@api";
import { queriesExternalBankAccounts } from "@query";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { useWSMutation } from "../../query/helpers";
import { track } from "../../utils/analytics";
import { WSServiceError } from "../../utils/serviceHelper";

type Config = {
  onSuccess: (externalBankAccounts: ExternalBankAccount[]) => void;
  onExit?: (error: any, meta: any) => void;
};

/**
 * v3 Plaid link flow for external bank accounts: mints a link token
 * (`POST /external-bank-accounts/link`) on the first `open()` call, opens
 * Plaid Link, and exchanges the returned public token for one external bank
 * account per account the user selected in Link
 * (`POST /external-bank-accounts` with `publicToken` + `plaidAccountId` —
 * one create per selected account).
 */
export const useExternalBankAccountsPlaidLink = (config: Config) => {
  const { openSnackbar } = useWSSnackbar();
  const [linkToken, setLinkToken] = useState<string>();
  const [openPending, setOpenPending] = useState(false);
  const tokenRequestedRef = useRef(false);

  const requestLinkToken = useCallback(() => {
    if (tokenRequestedRef.current) {
      return;
    }
    tokenRequestedRef.current = true;

    externalBankAccountsApi
      .createBankAccountLinkToken({ bankAccountLinkRequest: {} })
      .then(token => {
        setLinkToken(token.linkToken);
      })
      .catch(error => {
        tokenRequestedRef.current = false;
        console.error("Failed to create bank account link token", error);
        openSnackbar({
          type: "error",
          message: "Failed to start bank linking. Please try again."
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [exchangePublicToken, exchangePublicTokenMeta] = useWSMutation<
    ExternalBankAccount[],
    WSServiceError,
    { publicToken: string; metadata: PlaidLinkOnSuccessMetadata }
  >(
    async ({ publicToken, metadata }) => {
      const createdAccounts: ExternalBankAccount[] = [];

      for (const plaidAccount of metadata.accounts) {
        const idempotencyKey = new IdempotencyKey({
          publicToken,
          plaidAccountId: plaidAccount.id
        });

        createdAccounts.push(
          await externalBankAccountsApi.createExternalBankAccount({
            idempotencyKey: idempotencyKey.value,
            externalBankAccountCreate: {
              publicToken,
              plaidAccountId: plaidAccount.id
            }
          })
        );

        idempotencyKey.destroy();
      }

      return createdAccounts;
    },
    {
      dependencies: [
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase
      ]
    }
  );

  const plaidLink = usePlaidLink({
    token: linkToken ?? null,
    onExit(error: any, meta: any) {
      config.onExit?.(error, meta);
    },
    async onSuccess(publicToken, metadata) {
      openSnackbar({
        message: "Linking accounts",
        icon: {
          name: "loader"
        }
      });

      const createdAccounts = await exchangePublicToken({
        publicToken,
        metadata
      });

      if (createdAccounts) {
        config.onSuccess(createdAccounts);
      } else {
        openSnackbar({
          type: "error",
          message: `Linking failed. Plaid was unable to successfully connect to ${
            metadata.institution?.name ?? "your bank"
          }. Please try again or contact member support.`
        });
      }
    },
    onEvent(name: string, event: any) {
      track("Plaid Link Event Occurred", {
        eventName: name,
        ...event
      });

      // Plaid renders its iframe behind app modals unless bumped; mirror the
      // z-index workaround from the v1 hook.
      // https://github.com/plaid/react-plaid-link/issues/110
      switch (name) {
        case "OPEN": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(
              el => ((el as HTMLIFrameElement).style.zIndex = "2147483647")
            );
          break;
        }
        case "EXIT": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(el => ((el as HTMLIFrameElement).style.zIndex = "-999"));
          document.body.style.overflow = "";
          break;
        }
        default: {
        }
      }
    }
  });

  useEffect(() => {
    if (openPending && plaidLink.ready) {
      setOpenPending(false);
      plaidLink.open();
    }
  }, [openPending, plaidLink]);

  const open = useCallback(() => {
    if (plaidLink.ready) {
      plaidLink.open();
    } else {
      requestLinkToken();
      setOpenPending(true);
    }
  }, [plaidLink, requestLinkToken]);

  return {
    open,
    ready: plaidLink.ready,
    meta: exchangePublicTokenMeta
  };
};
